import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Plus, Pencil, Trash2, Anchor, Loader2 } from 'lucide-react';
import { useAdminPorts, type AdminPort } from '@/hooks/useAdminPorts';
import { PortFormDialog } from './PortFormDialog';

export const AdminPorts = () => {
  const { data: ports = [], isLoading, create, update, remove } = useAdminPorts();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<AdminPort | null>(null);
  const [search, setSearch] = useState('');

  const filtered = ports.filter((p) => {
    const s = search.toLowerCase();
    if (!s) return true;
    return (
      p.nom.toLowerCase().includes(s) ||
      (p.ville || '').toLowerCase().includes(s) ||
      (p.departement || '').toLowerCase().includes(s) ||
      (p.region || '').toLowerCase().includes(s)
    );
  });

  const handleSubmit = async (data: Partial<AdminPort>) => {
    if (editing) await update.mutateAsync({ id: editing.id, ...data });
    else await create.mutateAsync(data);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-4 flex-wrap">
        <CardTitle className="flex items-center gap-2">
          <Anchor className="h-5 w-5 text-primary" />
          Gestion des ports
          <Badge variant="secondary">{ports.length}</Badge>
        </CardTitle>
        <Button onClick={() => { setEditing(null); setOpen(true); }}>
          <Plus className="h-4 w-4 mr-1" /> Ajouter
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        <Input
          placeholder="Rechercher (nom, ville, département, région…)"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-md"
        />

        {isLoading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-center text-muted-foreground py-10">Aucun port.</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nom</TableHead>
                  <TableHead>Ville</TableHead>
                  <TableHead>Département</TableHead>
                  <TableHead>Pays</TableHead>
                  <TableHead>VHF</TableHead>
                  <TableHead>Téléphone</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell className="font-medium">{p.nom}</TableCell>
                    <TableCell>{p.ville || '—'}</TableCell>
                    <TableCell>{p.departement || '—'}</TableCell>
                    <TableCell>{p.pays}</TableCell>
                    <TableCell>{p.vhf_canal || '—'}</TableCell>
                    <TableCell className="text-sm">{p.telephone || '—'}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="icon" variant="ghost" onClick={() => { setEditing(p); setOpen(true); }}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button size="icon" variant="ghost">
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Supprimer ce port ?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Cette action est irréversible : « {p.nom} » et tous les professionnels associés seront supprimés.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Annuler</AlertDialogCancel>
                              <AlertDialogAction onClick={() => remove.mutate(p.id)}>
                                Supprimer
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        <PortFormDialog
          open={open}
          onOpenChange={setOpen}
          port={editing}
          onSubmit={handleSubmit}
        />
      </CardContent>
    </Card>
  );
};

export default AdminPorts;
